import axios from "axios";
import { decodeProcessingResult } from "../modules/encoding.js";

// Base API URL that adapts to the environment
const API_BASE_URL =
    process.env.NODE_ENV === "development"
        ? "http://localhost:5173/api"
        : "/api";

// Custom error class for processing errors
class ProcessingError extends Error {
    constructor(message) {
        super(message);
        this.name = "ProcessingError";
    }
}

// Auto logout function reference (will be set from inference.js)
let autoLogoutCallback = null;

// Authentication status (will be set from inference.js)
let isAuthenticationEnabled = true; // Default to true for safety

// Logout state tracking to prevent multiple simultaneous logouts
let isLoggingOut = false;
let logoutTimestamp = 0;

// Set up axios response interceptor to handle 401 errors globally
axios.interceptors.response.use(
    (response) => {
        // Return successful responses as-is
        return response;
    },
    (error) => {
        // Handle 401 Unauthorized responses
        if (error.response && error.response.status === 401) {
            console.log("🚪 Received 401 response - checking auth status...");

            // Only trigger logout if authentication is enabled and not already logging out
            if (isAuthenticationEnabled && !isLoggingOut) {
                const now = Date.now();
                // Prevent rapid successive logout attempts (within 5 seconds)
                if (now - logoutTimestamp < 5000) {
                    console.log(
                        "🚪 Recent logout attempt - skipping duplicate for 401"
                    );
                    return Promise.reject(error);
                }

                console.log(
                    "🚪 Authentication enabled - triggering auto logout"
                );
                isLoggingOut = true;
                logoutTimestamp = now;

                // Call the auto logout callback if available
                if (autoLogoutCallback) {
                    // Use setTimeout to prevent immediate re-entry and allow other requests to see the logout state
                    setTimeout(() => {
                        autoLogoutCallback();
                    }, 100);
                } else {
                    // Fallback: clear token cookie and show error
                    document.cookie =
                        "access_token=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/";
                    console.warn(
                        "Auto logout callback not set - token cleared"
                    );
                    setTimeout(() => {
                        isLoggingOut = false;
                    }, 2000);
                }
            } else if (isLoggingOut) {
                console.log(
                    "🚪 Already logging out - skipping duplicate logout for 401"
                );
            } else {
                console.log(
                    "🚪 Authentication disabled - skipping auto logout for 401"
                );
            }
        }

        // Re-throw the error for the caller to handle
        return Promise.reject(error);
    }
);

/**
 * Set the auto logout callback function
 * @param {Function} callback - Function to call on 401 responses
 */
function setAutoLogoutCallback(callback) {
    autoLogoutCallback = callback;
}

/**
 * Set the authentication enabled status
 * @param {boolean} enabled - Whether authentication is enabled
 */
function setAuthenticationEnabled(enabled) {
    isAuthenticationEnabled = enabled;
}

/**
 * Get access token from the most reliable source (prioritizing input field, fallback to cookie)
 * @returns {string|null} Access token or null if not found/invalid
 */
function getAccessToken() {
    // First try to get from input field (most up-to-date)
    const accessTokenInput = document.getElementById("access-token");
    let token = accessTokenInput?.value;

    // If input field is empty or invalid, try cookie
    if (
        !token ||
        token.trim() === "" ||
        token === "null" ||
        token === "undefined"
    ) {
        const cookieToken = document.cookie
            .split("; ")
            .find((row) => row.startsWith("access_token="));

        if (cookieToken) {
            token = cookieToken.split("=")[1];
        }
    }

    // Validate token
    if (
        !token ||
        token.trim() === "" ||
        token === "null" ||
        token === "undefined"
    ) {
        return null;
    }

    return token;
}

/**
 * Synchronize token between cookie and input field
 * @param {string|null} token - Token to sync (if null, will clear both)
 */
function syncToken(token) {
    const accessTokenInput = document.getElementById("access-token");

    if (token && token !== "null" && token !== "undefined") {
        // Set cookie with token
        document.cookie = `access_token=${token}; path=/; SameSite=Lax`;

        // Set input field
        if (accessTokenInput) {
            accessTokenInput.value = token;
        }
    } else {
        // Clear both cookie and input field
        document.cookie =
            "access_token=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/";

        if (accessTokenInput) {
            accessTokenInput.value = "";
        }
    }
}

/**
 * Reset logout state (called after successful logout)
 */
function resetLogoutState() {
    isLoggingOut = false;
    logoutTimestamp = 0;
}

/**
 * API Service class to handle all API interactions
 */
class ApiService {
    /**
     * Check if access token is valid and trigger logout if empty
     * @param {boolean} triggerLogout - Whether to trigger auto logout on invalid token
     * @returns {boolean} True if token is valid, false otherwise
     */
    static isAccessTokenValid(triggerLogout = true) {
        const accessToken = getAccessToken();
        const isTokenEmpty = !accessToken;

        if (
            isTokenEmpty &&
            triggerLogout &&
            autoLogoutCallback &&
            isAuthenticationEnabled &&
            !isLoggingOut
        ) {
            console.log(
                "🚪 Empty access token detected - triggering auto logout"
            );
            const now = Date.now();
            if (now - logoutTimestamp >= 5000) {
                // Prevent rapid successive calls
                isLoggingOut = true;
                logoutTimestamp = now;
                setTimeout(() => autoLogoutCallback(), 100);
            }
            return false;
        }

        return !isTokenEmpty;
    }

    /**
     * Validate access token on page load and trigger logout if invalid
     * Should be called during application initialization
     */
    static async validateTokenOnLoad() {
        console.log("🔐 Validating access token on load...");

        // Get token and sync if found
        const accessToken = getAccessToken();
        if (accessToken) {
            syncToken(accessToken); // Ensure cookie and input field are synchronized
        }

        // Check if we have a valid token
        if (!this.isAccessTokenValid(false)) {
            console.log(
                "🚪 Invalid access token on load - checking auth status..."
            );
            if (
                autoLogoutCallback &&
                isAuthenticationEnabled &&
                !isLoggingOut
            ) {
                console.log(
                    "🚪 Authentication enabled - triggering auto logout"
                );
                isLoggingOut = true;
                logoutTimestamp = Date.now();
                setTimeout(() => autoLogoutCallback(), 100);
            } else if (!isAuthenticationEnabled) {
                console.log(
                    "🚪 Authentication disabled - skipping auto logout for invalid token"
                );
            }
            return false;
        }

        console.log("✅ Access token validation passed");
        return true;
    }

    /**
     * Get the authorization header using the access token
     * @returns {Object} Headers object with Authorization
     */
    static getAuthHeaders() {
        // Check token validity before returning headers
        this.isAccessTokenValid();

        const accessToken = getAccessToken();
        return {
            Authorization: accessToken ? `Bearer ${accessToken}` : ""
        };
    }

    /**
     * Get system status
     * @returns {Promise} Promise with system status data
     */
    static async getSystemStatus() {
        try {
            const response = await axios.get(`${API_BASE_URL}/system-status`);
            return response.data;
        } catch (error) {
            console.error("Error getting system status:", error);
            throw error;
        }
    }

    /**
     * Get feedback configuration
     * @returns {Promise} Promise with feedback configuration data
     */
    static async getFeedbackConfig() {
        try {
            const response = await axios.get(
                `${API_BASE_URL}/feedback-config`,
                {
                    headers: {
                        ...this.getAuthHeaders()
                    }
                }
            );
            return response.data;
        } catch (error) {
            console.error("Error getting feedback config:", error);
            throw error;
        }
    }

    /**
     * Upload a file for processing (supports both file upload and Azure path)
     * @param {File|null} file - The file to upload (for file mode)
     * @param {string|null} azurePath - The Azure blob path (for Azure mode)
     * @param {string} modelType - The model type to use (teethml, defectml, keypointml, etc.)
     * @param {string} jawSelection - The jaw selection (upper, lower)
     * @param {boolean} autoOrientation - TeethML only: rotate a scan exported with a
     *     different axis convention into the frame the models were trained in
     * @returns {Promise} Promise with upload response data
     */
    static async uploadForProcessing(
        file,
        azurePath,
        modelType,
        jawSelection,
        autoOrientation = true
    ) {
        try {
            const formData = new FormData();
            formData.append("model", modelType);
            formData.append("jaw", jawSelection);
            if (modelType === "teethml") {
                formData.append("auto_orientation", String(autoOrientation));
            }

            if (file) {
                formData.append("file", file);
            } else if (azurePath) {
                formData.append("azure_path", azurePath);
            } else {
                throw new Error("Either file or Azure path must be provided");
            }

            const response = await axios.post(
                `${API_BASE_URL}/upload`,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                        ...this.getAuthHeaders()
                    }
                }
            );

            // Log response for debugging
            console.log("Upload response:", response.data);
            if (response.data.warning) {
                console.log("Warning in response:", response.data.warning);
            }

            // Return response data with warning if present
            return response.data;
        } catch (error) {
            console.error("Error uploading file:", error);

            // Extract detailed error message from response
            if (error.response && error.response.data) {
                const errorDetail =
                    error.response.data.detail ||
                    error.response.data.message ||
                    error.message;
                const enhancedError = new Error(errorDetail);
                enhancedError.response = error.response;
                enhancedError.status = error.response.status;
                throw enhancedError;
            }

            throw error;
        }
    }

    /**
     * Validate file type for model (without processing)
     * @param {File|null} file - The file to validate (for file mode)
     * @param {string|null} azurePath - The Azure blob path (for Azure mode)
     * @param {string} modelType - Model type ('teethml', 'defectml', 'keypointml')
     * @param {string|null} jawSelection - Jaw selection ('upper' or 'lower')
     * @returns {Promise} Promise with validation response containing optional warning
     */
    static async validateFileType(
        file,
        azurePath,
        modelType,
        jawSelection = null
    ) {
        try {
            const formData = new FormData();
            formData.append("model", modelType);
            if (jawSelection) {
                formData.append("jaw", jawSelection);
            }

            if (file) {
                formData.append("file", file);
            } else if (azurePath) {
                formData.append("azure_path", azurePath);
            } else {
                throw new Error("Either file or Azure path must be provided");
            }

            const response = await axios.post(
                `${API_BASE_URL}/validate-file-type`,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                        ...this.getAuthHeaders()
                    }
                }
            );

            return response.data;
        } catch (error) {
            console.error("Error validating file type:", error);
            // Don't throw - validation errors shouldn't block file selection
            return {};
        }
    }

    /**
     * List the immediate files in a single Azure folder (prefix).
     * @param {string} folderPath - The Azure folder path (e.g. "azure://container/case/model/")
     * @returns {Promise<Object>} { folder, container, prefix, files: [{ name, key, reference, size }] }
     * @throws Rethrows so the caller can surface the resolution error to the user.
     */
    static async listAzureFolder(folderPath) {
        try {
            const formData = new FormData();
            formData.append("folder_path", folderPath);

            const response = await axios.post(
                `${API_BASE_URL}/list-azure-folder`,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                        ...this.getAuthHeaders()
                    }
                }
            );

            return response.data;
        } catch (error) {
            console.error("Error listing Azure folder:", error);
            throw error;
        }
    }

    /**
     * Upload files for teeth reconstruction (supports both file upload and Azure path)
     * @param {File|null} meshFile - The mesh file (for file mode)
     * @param {string|null} meshAzurePath - The Azure blob path for mesh file (for Azure mode)
     * @param {File|Blob|null} labelsFile - The labels file (optional, for file mode)
     * @param {string|null} labelsAzurePath - The Azure blob path for labels file (optional, for Azure mode)
     * @returns {Promise} Promise with upload response data
     */
    static async uploadForReconstruction(
        meshFile,
        meshAzurePath,
        labelsFile = null,
        labelsAzurePath = null
    ) {
        try {
            const formData = new FormData();

            if (meshFile) {
                formData.append("file", meshFile);
            } else if (meshAzurePath) {
                formData.append("azure_path", meshAzurePath);
            } else {
                throw new Error(
                    "Either mesh file or Azure path must be provided"
                );
            }

            if (labelsFile) {
                formData.append("labels_file", labelsFile);
            } else if (labelsAzurePath) {
                formData.append("labels_azure_path", labelsAzurePath);
            }

            const response = await axios.post(
                `${API_BASE_URL}/reconstruct`,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                        ...this.getAuthHeaders()
                    }
                }
            );

            return response.data;
        } catch (error) {
            console.error("Error uploading for reconstruction:", error);
            throw error;
        }
    }

    /**
     * Upload files for teeth arrangement (supports both file upload and Azure path)
     * @param {File|null} primaryFile - The primary file (.obj or .pth) (for file mode)
     * @param {string|null} primaryAzurePath - The Azure blob path for primary file (for Azure mode)
     * @param {File|null} pairedFile - The paired file (optional, for jaw arrangement, for file mode)
     * @param {string|null} pairedAzurePath - The Azure blob path for paired file (optional, for Azure mode)
     * @param {File|null} infoJsonFile - Optional InfoJSON file (for file mode)
     * @param {string|null} infoJsonAzurePath - Optional InfoJSON Azure path (for Azure mode)
     * @param {File|null} landmarksCsvFile - Optional malocclusion landmarks CSV file (for file mode)
     * @param {string|null} landmarksCsvAzurePath - Optional malocclusion landmarks CSV Azure path (for Azure mode)
     * @param {Object} options - Arrangement options (config_file, weight_file, postprocessing, etc.)
     * @param {boolean} enablePostprocessing - Enable postprocessing flag (default: false)
     * @returns {Promise} Promise with upload response data
     */
    static async uploadForArrangement(
        primaryFile,
        primaryAzurePath,
        pairedFile = null,
        pairedAzurePath = null,
        infoJsonFile = null,
        infoJsonAzurePath = null,
        landmarksCsvFile = null,
        landmarksCsvAzurePath = null,
        options = {},
        enablePostprocessing = false,
        enableLeveling = false
    ) {
        try {
            const formData = new FormData();

            if (primaryFile) {
                formData.append("file", primaryFile);
            } else if (primaryAzurePath) {
                formData.append("azure_path", primaryAzurePath);
            } else {
                throw new Error(
                    "Either primary file or Azure path must be provided"
                );
            }

            if (pairedFile) {
                formData.append("paired_file", pairedFile);
            } else if (pairedAzurePath) {
                formData.append("paired_azure_path", pairedAzurePath);
            }

            // Add optional InfoJSON file or Azure path
            if (infoJsonFile) {
                formData.append("info_json_file", infoJsonFile);
            } else if (infoJsonAzurePath) {
                formData.append("info_json_azure_path", infoJsonAzurePath);
            }

            // Add optional landmarks CSV file or Azure path
            if (landmarksCsvFile) {
                formData.append("landmarks_csv_file", landmarksCsvFile);
            } else if (landmarksCsvAzurePath) {
                formData.append(
                    "landmarks_csv_azure_path",
                    landmarksCsvAzurePath
                );
            }

            formData.append("options", JSON.stringify(options));
            formData.append(
                "enable_postprocessing",
                enablePostprocessing ? "true" : "false"
            );
            formData.append(
                "enable_leveling",
                enableLeveling ? "true" : "false"
            );

            const response = await axios.post(
                `${API_BASE_URL}/arrangement`,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                        ...this.getAuthHeaders()
                    }
                }
            );

            return response.data;
        } catch (error) {
            console.error("Error uploading for arrangement:", error);
            throw error;
        }
    }

    /**
     * Upload inputs for arrangement refinement without model inference.
     * @param {File|null} primaryFile - Lower jaw OBJ file
     * @param {string|null} primaryAzurePath - Lower jaw Azure path
     * @param {File|null} pairedFile - Upper jaw OBJ file
     * @param {string|null} pairedAzurePath - Upper jaw Azure path
     * @param {Blob|File} infoJsonFile - Current arrangement InfoJSON
     * @param {File|null} landmarksCsvFile - Optional landmarks CSV file
     * @param {string|null} landmarksCsvAzurePath - Optional landmarks CSV Azure path
     * @param {Object} options - Postprocessing options payload
     * @returns {Promise<Object>}
     */
    static async uploadForArrangementRefine(
        primaryFile,
        primaryAzurePath,
        pairedFile,
        pairedAzurePath,
        infoJsonFile,
        landmarksCsvFile = null,
        landmarksCsvAzurePath = null,
        options = {}
    ) {
        try {
            const formData = new FormData();

            if (primaryFile) {
                formData.append("file", primaryFile);
            } else if (primaryAzurePath) {
                formData.append("azure_path", primaryAzurePath);
            } else {
                throw new Error(
                    "Either primary file or Azure path must be provided"
                );
            }

            if (pairedFile) {
                formData.append("paired_file", pairedFile);
            } else if (pairedAzurePath) {
                formData.append("paired_azure_path", pairedAzurePath);
            }

            if (!infoJsonFile) {
                throw new Error("Arrangement refine requires current InfoJSON");
            }
            formData.append(
                "info_json_file",
                infoJsonFile,
                "arrangement_info.json"
            );

            if (landmarksCsvFile) {
                formData.append("landmarks_csv_file", landmarksCsvFile);
            } else if (landmarksCsvAzurePath) {
                formData.append(
                    "landmarks_csv_azure_path",
                    landmarksCsvAzurePath
                );
            }

            formData.append("options", JSON.stringify(options));
            formData.append("enable_postprocessing", "true");

            const response = await axios.post(
                `${API_BASE_URL}/arrangement/refine`,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                        ...this.getAuthHeaders()
                    }
                }
            );

            return response.data;
        } catch (error) {
            console.error("Error uploading for arrangement refine:", error);
            throw error;
        }
    }

    /**
     * Upload inputs for leveling refinement (no model inference).
     * Mirrors uploadForArrangementRefine but targets POST /api/arrangement/level
     * with the LevelingRefiner-specific options payload (options.leveling).
     */
    static async uploadForArrangementLevel(
        primaryFile,
        primaryAzurePath,
        pairedFile,
        pairedAzurePath,
        infoJsonFile,
        landmarksCsvFile = null,
        landmarksCsvAzurePath = null,
        options = {}
    ) {
        try {
            const formData = new FormData();

            if (primaryFile) {
                formData.append("file", primaryFile);
            } else if (primaryAzurePath) {
                formData.append("azure_path", primaryAzurePath);
            } else {
                throw new Error(
                    "Either primary file or Azure path must be provided"
                );
            }

            if (pairedFile) {
                formData.append("paired_file", pairedFile);
            } else if (pairedAzurePath) {
                formData.append("paired_azure_path", pairedAzurePath);
            }

            if (!infoJsonFile) {
                throw new Error("Arrangement level requires current InfoJSON");
            }
            formData.append(
                "info_json_file",
                infoJsonFile,
                "arrangement_info.json"
            );

            if (landmarksCsvFile) {
                formData.append("landmarks_csv_file", landmarksCsvFile);
            } else if (landmarksCsvAzurePath) {
                formData.append(
                    "landmarks_csv_azure_path",
                    landmarksCsvAzurePath
                );
            }

            formData.append("options", JSON.stringify(options));
            formData.append("enable_leveling", "true");

            const response = await axios.post(
                `${API_BASE_URL}/arrangement/level`,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                        ...this.getAuthHeaders()
                    }
                }
            );

            return response.data;
        } catch (error) {
            console.error("Error uploading for arrangement level:", error);
            throw error;
        }
    }

    /**
     * Check processing status
     * @param {string} itemId - The processing item ID
     * @returns {Promise} Promise with status data
     */
    static async checkStatus(itemId) {
        try {
            const response = await axios.get(
                `${API_BASE_URL}/status/${itemId}`
            );
            return response.data;
        } catch (error) {
            console.error("Error checking status:", error);
            throw error;
        }
    }

    /**
     * Poll status until completion or timeout
     * @param {string} itemId - The processing item ID
     * @param {function} progressCallback - Callback to update progress (percent, timeRemaining)
     * @param {number} estimatedTime - Estimated processing time in seconds
     * @param {number} startTime - Processing start time (milliseconds)
     * @param {boolean} decodeResult - Whether to decode the result (default: true)
     * @returns {Promise} Promise with final result data
     */
    static async pollUntilCompletion(
        itemId,
        progressCallback,
        estimatedTime,
        startTime,
        decodeResult = true
    ) {
        let status = "processing";
        let pollCount = 0;

        console.log(
            `Starting polling for item ${itemId}, estimated time: ${estimatedTime}s (no timeout)`
        );

        while (status === "processing") {
            await new Promise((resolve) => setTimeout(resolve, 2000)); // Wait 2 seconds

            try {
                const statusData = await this.checkStatus(itemId);
                status = statusData.status;
                pollCount++;

                console.log(`Poll ${pollCount}: status = ${status}`);

                // Calculate remaining time
                const elapsedSeconds = (Date.now() - startTime) / 1000;
                const timeRemaining = Math.max(
                    0,
                    Math.round(estimatedTime - elapsedSeconds)
                );

                // Calculate progress percentage (10-90%)
                const timeBasedProgress =
                    ((estimatedTime - timeRemaining) / estimatedTime) * 100;
                const progressPercent = Math.max(
                    10,
                    Math.min(90, timeBasedProgress)
                );

                // Decide on time display text
                let timeDisplay;
                if (elapsedSeconds > estimatedTime) {
                    timeDisplay = "Taking longer than expected...";
                } else if (timeRemaining <= estimatedTime * 0.1) {
                    timeDisplay = "Almost done here...";
                } else {
                    timeDisplay = `Est. time: ~${timeRemaining}s`;
                }

                if (progressCallback) {
                    progressCallback(progressPercent, timeDisplay);
                }

                if (status === "completed") {
                    console.log(
                        `Processing completed after ${pollCount} polls (${Math.round(
                            elapsedSeconds
                        )}s)`
                    );
                    if (progressCallback) {
                        progressCallback(100, 0); // Complete
                    }

                    // Return decoded or raw result based on parameter
                    return decodeResult
                        ? decodeProcessingResult(statusData.result)
                        : statusData.result;
                } else if (status === "error") {
                    console.error(
                        `Processing failed after ${pollCount} polls:`,
                        statusData.error_reason
                    );
                    // This is a processing error from the server - throw immediately
                    const errorMessage =
                        statusData.error_reason || "Processing failed";
                    throw new ProcessingError(errorMessage);
                }
            } catch (error) {
                // If this is a 401 error, it will be handled by the axios interceptor
                if (error.response && error.response.status === 401) {
                    console.error("Authentication error during polling");
                    throw error; // Let auth errors bubble up immediately
                } else if (error instanceof ProcessingError) {
                    // This is a processing error from the server, not a network error
                    console.error("Server processing error:", error.message);
                    throw new Error(error.message);
                } else if (error.response) {
                    // HTTP error response - this is likely a temporary server issue, continue polling
                    console.warn(
                        `HTTP error during polling (attempt ${pollCount}):`,
                        error.response.status,
                        error.message
                    );
                    continue;
                } else {
                    // Network error or other connectivity issue - continue polling
                    console.warn(
                        `Network error during polling (attempt ${pollCount}):`,
                        error.message
                    );
                    continue;
                }
            }
        }

        // This should never be reached due to the infinite loop, but kept for safety
        throw new Error(`Processing ended with unexpected status: ${status}`);
    }

    /**
     * Submit feedback data to the server
     * @param {FormData} formData - FormData containing feedback and files
     * @returns {Promise} Promise with submission response data
     */
    static async submitFeedback(formData) {
        try {
            console.log("📝 Submitting feedback to server...");

            const response = await axios.post(
                `${API_BASE_URL}/feedback`,
                formData,
                {
                    headers: {
                        ...this.getAuthHeaders()
                        // Don't set Content-Type, let axios handle it for FormData
                    },
                    // Set a longer timeout for file uploads
                    timeout: 300000 // 5 minutes
                }
            );

            console.log("📝 Feedback submitted successfully:", response.data);
            return response.data;
        } catch (error) {
            console.error("📝 Error submitting feedback:", error);

            if (error.response) {
                // Server responded with error status
                const errorMessage =
                    error.response.data?.detail ||
                    error.response.data?.message ||
                    `Server error (${error.response.status})`;
                throw new Error(errorMessage);
            } else if (error.request) {
                // Request was made but no response received
                throw new Error(
                    "No response from server. Please check your connection."
                );
            } else {
                // Something else happened
                throw new Error(error.message || "Unknown error occurred");
            }
        }
    }

    /**
     * Record an arrangement InfoJSON accept/reject review decision.
     * The write is blocking on the server: the returned promise rejects if the
     * decision could not be persisted, so callers must not download on failure.
     * The reason for a rejection is captured separately via the feedback form.
     * @param {string} itemId - The task/item id of the arrangement job
     * @param {string} decision - "accept" or "reject"
     * @returns {Promise} Promise with the recorded decision
     */
    static async submitReviewDecision(itemId, decision) {
        try {
            const formData = new FormData();
            formData.append("item_id", itemId);
            formData.append("decision", decision);

            const response = await axios.post(
                `${API_BASE_URL}/arrangement/review`,
                formData,
                {
                    headers: {
                        ...this.getAuthHeaders()
                    }
                }
            );
            return response.data;
        } catch (error) {
            console.error("Error recording review decision:", error);

            if (error.response) {
                const errorMessage =
                    error.response.data?.detail ||
                    error.response.data?.message ||
                    `Server error (${error.response.status})`;
                throw new Error(errorMessage);
            } else if (error.request) {
                throw new Error(
                    "No response from server. Please check your connection."
                );
            } else {
                throw new Error(error.message || "Unknown error occurred");
            }
        }
    }

    /**
     * Generate signed URLs for downloading Azure files
     * @param {string|Array<string>} azurePaths - Single Azure path or array of paths
     * @param {number} expiryHours - Hours until the signed URLs expire (default: 1)
     * @returns {Promise} Promise with signed URL data
     */
    static async generateAzureDownloadUrls(azurePaths, expiryHours = 1) {
        try {
            console.log("🔗 Generating Azure download URLs:", azurePaths);

            const formData = new FormData();
            formData.append("azure_paths", JSON.stringify(azurePaths));
            formData.append("expiry_hours", expiryHours.toString());

            const response = await axios.post(
                `${API_BASE_URL}/azure-download-url`,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                        ...this.getAuthHeaders()
                    }
                }
            );

            console.log(
                "🔗 Azure download URLs generated successfully:",
                response.data
            );
            return response.data;
        } catch (error) {
            console.error("🔗 Error generating Azure download URLs:", error);

            if (error.response) {
                // Server responded with error status
                const errorMessage =
                    error.response.data?.detail ||
                    error.response.data?.message ||
                    `Server error (${error.response.status})`;
                throw new Error(errorMessage);
            } else if (error.request) {
                // Request was made but no response received
                throw new Error(
                    "No response from server. Please check your connection."
                );
            } else {
                // Something else happened
                throw new Error(error.message || "Unknown error occurred");
            }
        }
    }

    /**
     * Download file from Azure using signed URL and return as a File object
     * @param {string} azurePath - Azure blob path
     * @param {number} expiryHours - Hours until the signed URL expires (default: 1)
     * @returns {Promise<File>} Promise that resolves to a File object
     */
    static async downloadAzureFile(azurePath, expiryHours = 1) {
        try {
            console.log("📥 Downloading Azure file:", azurePath);

            // Generate signed URL
            console.log("🔗 Generating signed URL...");
            const urlData = await this.generateAzureDownloadUrls(
                azurePath,
                expiryHours
            );
            console.log("🔗 Signed URL generated:", urlData);

            if (!urlData.signed_url) {
                throw new Error("No signed URL received from server");
            }

            const signedUrl = urlData.signed_url;

            // Download the file using the signed URL
            console.log("⬇️ Downloading file from signed URL...");
            const response = await axios.get(signedUrl, {
                responseType: "blob"
            });

            console.log("⬇️ Download response:", {
                status: response.status,
                contentType: response.headers["content-type"],
                dataSize: response.data?.size || "unknown"
            });

            if (!response.data || response.data.size === 0) {
                throw new Error("Downloaded file is empty or invalid");
            }

            // Extract filename from the Azure path
            const filename = azurePath.split("/").pop() || "downloaded_file";

            // Create and return File object
            const file = new File([response.data], filename, {
                type:
                    response.headers["content-type"] ||
                    "application/octet-stream"
            });

            console.log("📥 Azure file downloaded successfully:", {
                filename: filename,
                fileSize: file.size,
                fileType: file.type
            });

            return file;
        } catch (error) {
            console.error("📥 Error downloading Azure file:", error);
            if (error.response) {
                console.error("📥 Response error details:", {
                    status: error.response.status,
                    statusText: error.response.statusText,
                    data: error.response.data
                });
            }
            throw error;
        }
    }
}

export {
    API_BASE_URL,
    ApiService,
    setAutoLogoutCallback,
    setAuthenticationEnabled,
    getAccessToken,
    syncToken,
    resetLogoutState
};
